import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { useParams } from 'react-router';
import { API, Auth } from 'aws-amplify';

import { editTextReset } from 'src/store/modules/editText';
import {
  buttonsReset,
  editBtnBgColor,
  editBtnColor,
  editBtnFontSize,
  editBtnFontWeight,
  editBtnPreFontSize,
  editBtnText
} from 'src/store/modules/buttons';
import {
  imageReset,
  editBgImage,
  editRadius,
  editDeeplink
} from 'src/store/modules/imageUpload';

import { pagesReset } from 'src/store/modules/pages';

import setInitValue from 'src/utils/setInitValue';
import { changeText } from 'src/utils/changeText';
import { editUserCompany } from 'src/store/modules/client';
import { trackingReset } from 'src/store/modules/trackings';
import { getFontURL } from 'src/utils/getFontUrl';
import { initTrackingValues } from '../TemplateTracking/SimpleModalTracking';

const initViewValues = {
  bgImage: {
    background: 'https://image.inappmessage.com/sample/defaultGRAYIMAGE.webp'
  },
  bgOpacity: { bgDimmed: 70 },
  radius: { background: 20, button: 20, never_show_button: 0 },
  deeplink: { background: 'https://www.inappmessage.com/' },

  btnText: { button: 'See more', never_show_button: "Dont't ask again" },
  btnFontSize: { button: 1, never_show_button: 0.875 },
  btnFontWeight: { button: 600, never_show_button: 600 },
  btnColor: { button: '#ffffff', never_show_button: '#ffffff' },
  btnBgColorR: { button: 95, never_show_button: 0 },
  btnBgColorG: { button: 130, never_show_button: 0 },
  btnBgColorB: { button: 146, never_show_button: 0 },
  btnBgColorA: { button: 1, never_show_button: 0 },
  btnFontFamily: { button: 'Montserrat', never_show_button: 'Montserrat' },
  btnAgain: {
    type: 'none',
    never_show_button: "don't show"
  }
};

const SimpleModal: FC = () => {
  const dispatch = useDispatch();
  const [currentUser, setCurrentUser] = useState('');
  useEffect(() => {
    async function getUserId() {
      try {
        const current = await Auth.currentUserInfo();
        setCurrentUser(current.attributes['custom:Company']);
        dispatch(editUserCompany(current.attributes['custom:Company']));
      } catch (error) {
        console.log('error');
      }
    }
    getUserId();
  }, []);

  const clientName = useSelector(
    (state: RootState) => state.client.userCompany
  );
  const bgImage = useSelector((state: RootState) => state.imageUpload.bgImage);
  const radius = useSelector((state: RootState) => state.imageUpload.radius);

  const btnText = useSelector((state: RootState) => state.buttons.btnText);
  const btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  const btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  const btnColor = useSelector((state: RootState) => state.buttons.btnColor);
  const btnBgColorR = useSelector(
    (state: RootState) => state.buttons.btnBgColorR
  );
  const btnBgColorG = useSelector(
    (state: RootState) => state.buttons.btnBgColorG
  );
  const btnBgColorB = useSelector(
    (state: RootState) => state.buttons.btnBgColorB
  );
  const btnBgColorA = useSelector(
    (state: RootState) => state.buttons.btnBgColorA
  );
  const btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );
  const bgOpacity = useSelector(
    (state: RootState) => state.imageUpload.bgImageBlur
  );

  const backgroundDimmed = bgOpacity?.bgDimmed / 100;
  const displayValue = backgroundDimmed >= 0.01 ? backgroundDimmed : 0.7;

  const btnFontURL = getFontURL(btnFontFamily?.button);
  const neverShowBtnFontURL = getFontURL(btnFontFamily?.never_show_button);

  const closeImageUrl = 'https://image.inappmessage.com/sample/xmark-1.png';
  const style = `
  @Font-face {
    font-family:'btn';
    src: url(${btnFontURL});
  }
  @Font-face {
    font-family: 'never_show_btn';
    src: url(${neverShowBtnFontURL});
  }
    .root_view {
      width: 100%;
      height: 100%;
      
      position: relative;
      background: none;
      overflow: hidden;
      
      .close_btn {
        position: absolute;
        right: 10%;
        top: 6%;
      }
      .close_btn button {
        width: 1.5rem;
        height: 1.5rem;
        
        background-image: url(${closeImageUrl});
        background-repeat: no-repeat;
        background-size: 1.5rem;
        background-position: center;

        font-size: 0;
        border: none;
        background-color: transparent;
      }

      .background {
        width: 100%;
        height: 100%;
  
        position: absolute;
        top: 0;
        background: rgba(0, 0, 0, ${displayValue});
      }
      .center {
        width: 290px;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
  
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      .modal_wrapper {
        width: 100%;
        height: 435px;
        padding: 2rem 1rem;

        border-radius: ${radius?.background}px;
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center;
        position: relative;
        ${
          bgImage?.background || bgImage?.background !== ''
            ? `background-image: url(${bgImage?.background});`
            : 'background: rgba(0, 0, 0, 0.8);'
        };     
      }

      .btn_wrapper {
        width: calc(100% - 2rem);
        position: absolute;
        bottom: 2rem;
      }
      .btn_wrapper button {
        width: 100%;
        height: 2.6rem;
        outline: 0;
        border: 0;

        font-size: ${btnFontSize?.button}rem;
        font-weight: ${btnFontWeight?.button};
        color: ${btnColor?.button};
        font-family: ${
          ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
            btnFontFamily?.button
          )
            ? btnFontFamily?.button
            : 'btn'
        };

        border-radius: ${radius?.button}px;
        background-color:rgba(${btnBgColorR?.button},${btnBgColorG?.button}, ${
    btnBgColorB?.button
  }, ${btnBgColorA?.button});
      }

      .bottom_wrapper {
        width: 100%;
        height: 2.6rem;

        display: flex;
        align-items: center;
        justify-content: center;

        text-align: center;
        font-size: ${btnFontSize?.never_show_button}rem;
        font-weight: ${btnFontWeight?.never_show_button};
        color: ${btnColor?.never_show_button};
        font-family: ${
          ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
            btnFontFamily?.never_show_button
          )
            ? btnFontFamily?.never_show_button
            : 'never_show_btn'
        };

        border-radius: ${radius?.never_show_button}px;
        background-color:rgba(${btnBgColorR?.never_show_button},${
    btnBgColorG?.never_show_button
  }, ${btnBgColorB?.never_show_button}, ${btnBgColorA?.never_show_button});
      }
    }
    `;

  const { id } = useParams<{ id: string }>();
  useEffect(() => {
    async function onLoad() {
      try {
        if (id) {
          const { customValues } = await API.get(
            'iam-editor',
            `/templates/${id}`,
            ''
          );
          if (
            customValues?.bgImage &&
            customValues?.text &&
            customValues?.btnText
          ) {
            setInitValue(dispatch, customValues);
          } else {
            setValue(
              dispatch,
              customValues,
              { ...initViewValues, ...initTrackingValues },
              clientName
            );
          }
        }
      } catch (error) {}
    }
    setInitValue(dispatch, { ...initViewValues, ...initTrackingValues });
    onLoad();
    return () => {
      dispatch(buttonsReset());

      dispatch(editTextReset());
      dispatch(imageReset());
      dispatch(pagesReset());
      dispatch(trackingReset());
    };
  }, []);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: style }} />
      <div className="root_view">
        <div className="background" />
        <div className="close_btn">
          <button>close</button>
        </div>
        <div className="center">
          <div className="modal_wrapper">
            <div className="btn_wrapper">
              <button>{btnText?.button}</button>
            </div>
          </div>
          <div className="bottom_wrapper">{btnText?.never_show_button}</div>
        </div>
      </div>
    </>
  );
};

const setValue = (
  dispatch: any,
  values: any,
  initViewValues: any,
  clientName: any
) => {
  // console.log(values);
  // console.log('test');
  // if (values?.bgColor) dispatch(editBgColor(values?.bgColor));
  dispatch(
    editBgImage({
      background: `https://image.inappmessage.com/${clientName}/${values?.imageName}`
    })
  );
  // if (values?.bgImageBlur) dispatch(editBgImageBlur(values?.bgImageBlur));
  dispatch(editDeeplink({ background: values?.deepLink }));
  if (values?.radius) {
    dispatch(
      editRadius({
        ...initViewValues,
        background: values?.imageRadius
      })
    );
  }

  dispatch(
    editBtnText({
      ...initViewValues,
      button: changeText(values?.mainBtnText),
      never_show_button: changeText(values?.bottomBtnText)
    })
  );
  dispatch(
    editBtnPreFontSize({
      ...initViewValues,
      button: values?.mainBtnTextSize / 16,
      never_show_button: values?.bottomBtnTextSize / 16
    })
  );
  dispatch(
    editBtnFontSize({
      ...initViewValues,
      button: values?.mainBtnTextSize / 16,
      never_show_button: values?.bottomBtnTextSize / 16
    })
  );
  dispatch(
    editBtnFontWeight({
      ...initViewValues,
      button: values?.mainBtnTextWeight,
      never_show_button: values?.bottomBtnTextWeight
    })
  );
  dispatch(
    editBtnColor({
      ...initViewValues,
      button: values?.mainBtnTextColor,
      never_show_button: '#ffffff'
    })
  );
  dispatch(
    editBtnBgColor(
      {
        ...initViewValues,
        button: values?.mainBtnBgColorR,
        never_show_button: values?.mainBtnBgColorR
      },
      {
        ...initViewValues,
        button: values?.mainBtnBgColorG,
        never_show_button: values?.mainBtnBgColorR
      },
      {
        ...initViewValues,
        button: values?.mainBtnBgColorB,
        never_show_button: values?.mainBtnBgColorR
      },
      {
        ...initViewValues,
        button: values?.mainBtnBgColorA,
        never_show_button: 0
      }
    )
  );
  // if (values?.btnFontFamily)
  //   dispatch(editBtnFontFamily(values?.btnFontFamily));
};

export default SimpleModal;
